import { supportsValuationHistory, type AccountSummary, type AccountType } from '@/api/accounts'

/** What an investment or property is worth over what went into it; null until a value is recorded. */
export function investmentGain(
  account: AccountSummary
): { amount: number; pct: number | null } | null {
  if (!supportsValuationHistory(account.type) || account.currentValue == null) return null
  const amount = account.currentValue - account.balance
  return {
    amount,
    pct: account.balance === 0 ? null : (amount / Math.abs(account.balance)) * 100,
  }
}

const SPENDABLE_TYPES: AccountType[] = ['BANK', 'CASH', 'DIGITAL_WALLET']
// Goals can only earmark plain accounts; of the spendable ones that's bank and cash.
const EARMARKABLE_TYPES: AccountType[] = ['BANK', 'CASH']

/**
 * Bank, cash and wallet balances minus the money set aside for goals in them. Null while any
 * bank or cash account's earmarks are unknown, so a half-loaded figure is never shown.
 */
export function readyToSpend(
  accounts: AccountSummary[],
  earmarked: Record<string, number | undefined>
): number | null {
  let total = 0
  for (const a of accounts) {
    if (a.archivedAt || !SPENDABLE_TYPES.includes(a.type)) continue
    total += a.balance
    if (EARMARKABLE_TYPES.includes(a.type)) {
      const set = earmarked[a.id]
      if (set === undefined) return null
      total -= set
    }
  }
  return total
}

const SUB_TYPE_LABELS: Record<string, string> = {
  SAVINGS: 'Savings',
  CURRENT: 'Current',
  SALARY: 'Salary',
  RD: 'RD',
  FD: 'FD',
  RETIREMENT: 'Retirement',
  MARKET_LINKED: 'Mutual funds / stocks',
}

export function subTypeLabel(subType: string): string {
  return SUB_TYPE_LABELS[subType] ?? subType.replace(/_/g, ' ').toLowerCase()
}
